import {
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  SelectChangeEvent,
} from '@mui/material';
import { useState, useContext } from 'react';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppDispatch, AppStore } from '../store';
import { ConfigurationApi } from '../api/ConfigurationApi';
import { SessionStore } from '../services/SessionStore';
import { AppAuthContext } from '../services/Contexts';
import { UrlHelper } from '../services';
import { EUrlParams, EReportType, ESheetType } from '../data/Constants';
import DraggableComponent from './DraggableComponent';
import { setError } from '../store/states/ErrorSlice';
import { isFeatureActive } from '../services/ClaimsHelper';
import type { IExportConfigurationSummaryProps } from '../../types/ITypes';

export const ExportConfigurationSummary = (props: IExportConfigurationSummaryProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState(false);
  const token = useContext(AppAuthContext);
  const configurationId =
    UrlHelper.getSearchParameter(EUrlParams.ConfigurationId) ?? SessionStore.get('configurationId');
  const [sheetType, setSheetType] = useState(ESheetType.Single);
  const [reportType, setReportType] = useState(EReportType.GCE);
  const featureFlags = AppStore.getState().claimsData.featureFlags;
  const isCu50SapFlagEnabled = isFeatureActive(featureFlags.ECForSAP);

  const exportSummary = () => {
    const excelSheetType = reportType === EReportType.CU50 ? null : sheetType;
    ConfigurationApi.exportConfigurationBomExcel({
      token,
      configurationId,
      sheetType: excelSheetType,
      reportType,
    }).then((response) => {
      if (response.error) {
        dispatch(setError(response.error));
      } else {
        const fileName = response.headers['content-disposition'].split('filename=')[1];
        const url = globalThis.URL.createObjectURL(
          new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
          })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}`);
        document.body.appendChild(link);
        link.click();
      }
    });
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleReportTypeChange = (e: SelectChangeEvent) => {
    setReportType(e.target.value as EReportType);
  };

  const handleSheetTypeChange = (e: SelectChangeEvent) => {
    setSheetType(e.target.value as ESheetType);
  };

  const getDialogContent = () => {
    return (
      <DialogContent className="action-panel-dialog-content shorten">
        {isCu50SapFlagEnabled && (
          <fieldset className="inputFieldset">
            <legend className="inputLegend">Format</legend>
            <RadioGroup
              aria-labelledby="dynamic-input-control"
              name="single-select-control-group"
              onChange={handleReportTypeChange}
              className="w-100"
              value={reportType}
            >
              <FormControlLabel
                label={t('exportConfigurationDialog.gce')}
                value={EReportType.GCE}
                control={<Radio />}
              />
              <FormControlLabel
                label={t('exportConfigurationDialog.cu50')}
                value={EReportType.CU50}
                control={<Radio />}
              />
            </RadioGroup>
          </fieldset>
        )}
        {reportType !== EReportType.CU50 && (
          <fieldset className="inputFieldset">
            <legend className="inputLegend">Type</legend>
            <RadioGroup
              aria-labelledby="dynamic-input-control"
              name="single-select-control-group"
              onChange={handleSheetTypeChange}
              className="w-100"
              value={sheetType}
            >
              <FormControlLabel
                label={t('exportConfigurationDialog.singleSheet')}
                value={ESheetType.Single}
                control={<Radio />}
              />
              <FormControlLabel
                label={t('exportConfigurationDialog.multipleSheet')}
                value={ESheetType.Multiple}
                control={<Radio />}
              />
            </RadioGroup>
          </fieldset>
        )}
      </DialogContent>
    );
  };

  return (
    <>
      <Tooltip title={t('labels.exportConfiguration')}>
        <IconButton
          className={`exportIcon ${props.type && props.type === 'detailed' ? 'detailedExportIcon' : 'quickExportIcon'}`}
          onClick={handleClickOpen}
          data-testid="exportIcon-btn"
        >
          <SystemUpdateAltIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        className="common-dialog-style cursor-move"
        sx={{
          '& .MuiDialog-container': { '& .MuiPaper-root': { width: '440px' } },
        }}
        open={open}
        PaperComponent={DraggableComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle className="header">{t('labels.exportConfiguration')}</DialogTitle>
        {getDialogContent()}
        <DialogActions>
          <Button
            className="text-capitalize"
            onClick={exportSummary}
            data-testid="exportSummary-btn"
          >
            {t('button.download')}
          </Button>
          <Button className="text-capitalize" aria-label="close" onClick={handleClose}>
            {t('button.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
